// Meta Pixel utility functions
// Pixel ID: 4907677319457935

// Safe wrapper to check if Meta Pixel is available
const isMetaPixelReady = () => {
  return typeof window !== 'undefined' && window.fbq && typeof window.fbq === 'function';
};

// Queue events if pixel isn't ready yet
const queuedEvents = [];
let pixelReady = false;

// Process queued events when pixel becomes available
const processQueuedEvents = () => {
  if (!pixelReady && isMetaPixelReady()) {
    pixelReady = true;
    queuedEvents.forEach(event => {
      try {
        window.fbq(event.type, event.name, event.data);
      } catch (error) {
        console.warn('Meta Pixel event failed:', error);
      }
    });
    queuedEvents.length = 0; // Clear queue
  }
};

// Safe tracking function with queueing
const safeTrack = (type, eventName, data = {}) => {
  if (isMetaPixelReady()) {
    try {
      window.fbq(type, eventName, data);
      pixelReady = true;
    } catch (error) {
      console.warn('Meta Pixel tracking failed:', error);
    }
  } else {
    // Queue event for later processing
    queuedEvents.push({ type, name: eventName, data });
    // Try to process queue periodically
    setTimeout(processQueuedEvents, 100);
  }
};

// Initialize Meta Pixel (called automatically by App.js)
export const initializeMetaPixel = () => {
  safeTrack('init', '4907677319457935');
  safeTrack('track', 'PageView');
};

// Track page view
export const trackPageView = () => {
  safeTrack('track', 'PageView');
};

// Track search
export const trackSearch = (searchTerm) => {
  if (!searchTerm) return; // Don't track empty searches
  safeTrack('track', 'Search', {
    search_string: searchTerm
  });
};

// Track product view
export const trackViewContent = (productData) => {
  if (!productData || !productData.id) return; // Validate input
  
  // Ensure value is a valid number greater than 0
  const value = parseFloat(productData.price);
  if (isNaN(value) || value <= 0) {
    console.warn('Meta Pixel: Invalid price value', productData.price);
    return;
  }
  
  safeTrack('track', 'ViewContent', {
    content_type: 'product',
    content_ids: [String(productData.id)],
    content_name: productData.name || '',
    value: value,
    currency: 'IQD'
  });
};

// Track add to cart
export const trackAddToCart = (productData) => {
  if (!productData || !productData.id) return; // Validate input
  
  // Ensure value is a valid number greater than 0
  const value = parseFloat(productData.price);
  if (isNaN(value) || value <= 0) {
    console.warn('Meta Pixel: Invalid price value', productData.price);
    return;
  }
  
  safeTrack('track', 'AddToCart', {
    content_type: 'product',
    content_ids: [String(productData.id)],
    content_name: productData.name || '',
    value: value,
    currency: 'IQD'
  });
};

// Track initiate checkout
export const trackInitiateCheckout = (cartItems, isWholesale = false) => {
  if (!cartItems || !Array.isArray(cartItems) || cartItems.length === 0) return;
  
  const content_ids = cartItems.map(item => String(item.id));
  const value = cartItems.reduce((total, item) => {
    const price = parseFloat(item.after_discount || item.discount_price || item.price || item.sell_price || 0);
    const quantity = parseInt(item.quantity) || 1;
    return total + (price * quantity);
  }, 0);

  // Ensure value is a valid number greater than 0
  if (isNaN(value) || value <= 0) {
    console.warn('Meta Pixel: Invalid checkout value', value);
    return;
  }

  safeTrack('track', 'InitiateCheckout', {
    content_type: 'product',
    content_ids: content_ids,
    value: value,
    currency: 'IQD',
    num_items: cartItems.length,
    custom_data: {
      store_type: isWholesale ? 'wholesale' : 'retail'
    }
  });
};

// Track purchase
export const trackPurchase = (orderData, isWholesale = false) => {
  if (!orderData || !orderData.items || !Array.isArray(orderData.items) || orderData.items.length === 0) return;
  
  // Try to get value from orderData.total first
  let value = parseFloat(orderData.total);
  
  // If orderData.total is invalid, calculate from items as fallback
  if (isNaN(value) || value <= 0) {
    console.warn('Meta Pixel: orderData.total is invalid, calculating from items', orderData.total);
    value = orderData.items.reduce((total, item) => {
      const price = parseFloat(item.after_discount || item.discount_price || item.price || item.sell_price || 0);
      const quantity = parseInt(item.quantity) || 1;
      return total + (price * quantity);
    }, 0);
  }
  
  // Only return if we still don't have a valid value after both attempts
  if (isNaN(value) || value <= 0) {
    console.warn('Meta Pixel: Could not determine valid purchase value', { orderData });
    return;
  }
  
  // Ensure value is a number with proper format (e.g., 9.99 or 9)
  value = parseFloat(value.toFixed(2));
  
  const content_ids = orderData.items.map(item => String(item.id));
  
  safeTrack('track', 'Purchase', {
    content_type: 'product',
    content_ids: content_ids,
    value: value,
    currency: 'IQD',
    transaction_id: String(orderData.order_id || ''),
    custom_data: {
      store_type: isWholesale ? 'wholesale' : 'retail',
      delivery_method: orderData.delivery_method || 'standard',
      payment_method: orderData.payment_method || 'cash'
    }
  });
};

// Track custom events
export const trackCustomEvent = (eventName, parameters = {}) => {
  if (!eventName) return;
  safeTrack('trackCustom', eventName, parameters);
};

// Track lead (for signups, contact forms, etc.)
export const trackLead = (leadData = {}) => {
  safeTrack('track', 'Lead', leadData);
};

// Track registration
export const trackCompleteRegistration = (userData = {}) => {
  safeTrack('track', 'CompleteRegistration', {
    status: 'completed',
    ...userData
  });
};